class BundleBuilder {
  constructor(form) {
    this.form = form;
    this.productForm = form.closest('product-form');
    this.submitBtn = form.querySelector('[name="add"]');
    this.config = this.getConfig();
    this.selected = new Map();
    this.modal = null;
  }

  getConfig() {
    const configEl = document.getElementById(`BundleBuilderData-${this.form.dataset.bundleConfigId}`);
    if (!configEl) return null;

    try {
      return JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('Bundle builder config could not be parsed.', error);
      return null;
    }
  }

  open() {
    if (!this.config || !this.config.products || !this.config.products.length) {
      this.showFormError('Bundle products are not configured for this product.');
      return;
    }

    this.selected.clear();
    this.modal = this.createModal();
    document.body.appendChild(this.modal);
    document.documentElement.classList.add('bundle-builder-is-open');
    this.updateState();

    window.requestAnimationFrame(() => {
      this.modal.classList.add('is-visible');
      const firstSelectable = this.modal.querySelector('.bundle-builder__card:not([aria-disabled="true"])');
      if (firstSelectable) firstSelectable.focus();
    });
  }

  close() {
    if (!this.modal) return;

    this.modal.classList.remove('is-visible');
    document.documentElement.classList.remove('bundle-builder-is-open');
    setTimeout(() => {
      if (this.modal) this.modal.remove();
      this.modal = null;
      if (this.submitBtn) this.submitBtn.focus();
    }, 180);
  }

  createModal() {
    const required = Number(this.config.requiredItems || this.form.dataset.bundleRequiredItems);
    const modal = document.createElement('div');
    modal.className = 'bundle-builder';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'BundleBuilderTitle');

    modal.innerHTML = `
      <div class="bundle-builder__overlay" data-bundle-close></div>
      <div class="bundle-builder__dialog">
        <div class="bundle-builder__header">
          <div>
            <h2 class="bundle-builder__title" id="BundleBuilderTitle">Build Your Bundle</h2>
            <p class="bundle-builder__message">Please select ${required} items to continue.</p>
          </div>
          <button type="button" class="bundle-builder__close" data-bundle-close aria-label="Close">X</button>
        </div>
        <p class="bundle-builder__count" aria-live="polite">Selected 0 of ${required}</p>
        <div class="bundle-builder__grid">
          ${this.config.products.map((product) => this.renderProductCard(product)).join('')}
        </div>
        <div class="bundle-builder__footer">
          <div class="alert bg-error-bg text-error-text bundle-builder__error" role="alert" hidden></div>
          <button type="button" class="btn btn--primary bundle-builder__add" disabled>Add Bundle to Cart</button>
        </div>
      </div>
    `;

    modal.addEventListener('click', this.handleModalClick.bind(this));
    modal.addEventListener('keydown', this.handleModalKeydown.bind(this));

    return modal;
  }

  renderProductCard(product) {
    const disabled = !product.available;
    const image = product.image
      ? `<img src="${product.image}" alt="${this.escapeHtml(product.title)}" loading="lazy">`
      : '<div class="bundle-builder__placeholder" aria-hidden="true"></div>';

    return `
      <button type="button"
        class="bundle-builder__card"
        data-bundle-product
        data-variant-id="${product.variantId}"
        data-title="${this.escapeHtml(product.title)}"
        ${disabled ? 'disabled aria-disabled="true"' : ''}>
        <span class="bundle-builder__image">${image}</span>
        <span class="bundle-builder__details">
          <span class="bundle-builder__product-title">${this.escapeHtml(product.title)}</span>
          <span class="bundle-builder__price">${this.escapeHtml(product.price)}</span>
          <span class="bundle-builder__choice">${disabled ? 'Unavailable' : 'Select'}</span>
        </span>
      </button>
    `;
  }

  handleModalClick(evt) {
    const closeBtn = evt.target.closest('[data-bundle-close]');
    if (closeBtn) {
      this.close();
      return;
    }

    const card = evt.target.closest('[data-bundle-product]');
    if (card) {
      this.toggleProduct(card);
      return;
    }

    if (evt.target.closest('.bundle-builder__add')) {
      this.addBundleToCart();
    }
  }

  handleModalKeydown(evt) {
    if (evt.key === 'Escape') this.close();
  }

  toggleProduct(card) {
    const variantId = Number(card.dataset.variantId);
    const required = Number(this.config.requiredItems);

    if (this.selected.has(variantId)) {
      this.selected.delete(variantId);
      card.classList.remove('is-selected');
      card.querySelector('.bundle-builder__choice').textContent = 'Select';
    } else if (this.selected.size < required) {
      this.selected.set(variantId, card.dataset.title);
      card.classList.add('is-selected');
      card.querySelector('.bundle-builder__choice').textContent = 'Selected';
    }

    this.updateState();
  }

  updateState() {
    const required = Number(this.config.requiredItems);
    const count = this.selected.size;
    const addBtn = this.modal.querySelector('.bundle-builder__add');
    const countEl = this.modal.querySelector('.bundle-builder__count');

    countEl.textContent = `Selected ${count} of ${required}`;
    addBtn.disabled = count !== required;

    this.modal.querySelectorAll('[data-bundle-product]').forEach((card) => {
      const isSelected = this.selected.has(Number(card.dataset.variantId));
      card.classList.toggle('is-disabled-by-limit', count >= required && !isSelected);
    });
  }

  async addBundleToCart() {
    const required = Number(this.config.requiredItems);
    if (this.selected.size !== required) return;

    const addBtn = this.modal.querySelector('.bundle-builder__add');
    const errorEl = this.modal.querySelector('.bundle-builder__error');
    const formData = new FormData(this.form);
    const selectedNames = Array.from(this.selected.values());
    const variantInput = this.form.querySelector('[name="id"]');
    const quantityInput = this.form.querySelector('[name="quantity"]');
    const bundleGroupId = `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const mainVariantId = Number(formData.get('id') || (variantInput ? variantInput.value : 0));
    const mainQuantity = Number(formData.get('quantity') || (quantityInput ? quantityInput.value : 1) || 1);

    addBtn.disabled = true;
    addBtn.classList.add('is-loading');
    errorEl.hidden = true;
    this.setSubmitLoading(true);

    const mainItem = {
      id: mainVariantId,
      quantity: mainQuantity,
      properties: this.getLineItemProperties(formData, selectedNames, required, bundleGroupId)
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

    const items = [
      mainItem,
      ...Array.from(this.selected.keys()).map((variantId) => ({
        id: variantId,
        quantity: mainQuantity,
        properties: {
          '_Bundle Group': bundleGroupId,
          '_Bundle Role': 'component',
          '_Bundle Parent Variant ID': String(mainVariantId),
          '_Bundle Required Quantity': String(required),
          '_Bundle Product URL': window.location.pathname
        }
      }))
    ];

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.description || data.message || theme.strings.cartError);

      await this.refreshCartUi();
      this.close();
    } catch (error) {
      errorEl.textContent = error.message || theme.strings.cartError;
      errorEl.hidden = false;
    } finally {
      addBtn.classList.remove('is-loading');
      addBtn.disabled = this.selected.size !== required;
      this.setSubmitLoading(false);
    }
  }

  getLineItemProperties(formData, selectedNames, required, bundleGroupId) {
    const properties = {};

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    });

    properties['Bundle Items'] = selectedNames.join(', ');
    properties['Bundle Required Quantity'] = String(required);
    properties['_Bundle Group'] = bundleGroupId;
    properties['_Bundle Role'] = 'parent';
    properties['_Bundle Required Quantity'] = String(required);
    properties['_Bundle Product URL'] = window.location.pathname;

    return properties;
  }

  async refreshCartUi() {
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      const sectionsToRender = cartDrawer.getSectionsToRender().map((section) => section.section);
      const sectionsResponse = await fetch(`${window.location.pathname}?sections=${sectionsToRender.join(',')}`);
      const sections = await sectionsResponse.json();
      await cartDrawer.renderContents({ sections }, true);
      return;
    }

    if (window.location.pathname === theme.routes.cart) {
      window.location.reload();
      return;
    }

    window.location.href = theme.routes.cart;
  }

  setSubmitLoading(isLoading) {
    if (!this.submitBtn) return;

    if (isLoading) {
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');
    } else {
      this.submitBtn.removeAttribute('aria-disabled');
      this.submitBtn.classList.remove('is-loading');
    }
  }

  showFormError(message) {
    const errorEl = this.productForm ? this.productForm.querySelector('.js-form-error') : null;
    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[char]));
  }
}

document.addEventListener('submit', (evt) => {
  const form = evt.target.closest('.js-product-form-main[data-bundle-builder="true"]');
  if (!form) return;

  evt.preventDefault();
  evt.stopPropagation();

  if (form.querySelector('[name="add"]')?.disabled) return;

  const builder = new BundleBuilder(form);
  builder.open();
}, true);
